import { Badge, Box, Button, ButtonGroup, FormControlLabel, IconButton, Stack, Typography } from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import { makeStyles } from '@mui/styles';
import {
  IconArrowsMaximize,
  IconArrowsMinimize,
  IconCamera,
  IconCameraOff,
  IconChevronDown,
  IconChevronUp,
  IconMicrophone,
  IconMicrophoneOff,
  IconNote,
  IconPlayerPlay,
  IconPlayerStop,
  IconScreenShare,
  IconScreenShareOff,
  IconUsers,
  IconVolume,
  IconVolume3,
  IconWriting
} from '@tabler/icons';
import useAuth from 'hooks/useAuth';
import useUser from 'hooks/useUser';
import moment from 'moment';
import 'moment-timezone';
import { useSnackbar } from 'notistack';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { browserName } from 'react-device-detect';
import Draggable from 'react-draggable';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { FormattedMessage, useIntl } from 'react-intl';
import { useMutation } from 'react-query';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { useSearchParam } from 'react-use';
import useStateRef from 'react-usestateref';
import { SET_MENU } from 'store/actions';
import MainCard from 'views/components/cards/MainCard';
import CenteredLoading from 'views/components/CenteredLoading';
import BasicDialog from 'views/components/modals/BasicDialog';
import CameraPreview from './CameraPreview';
import ControlButton from './ControlButton';
import LayoutButton from './LayoutButton';
import ScreenName from './ScreenName';
import SessionSideDrawer from './SessionSideDrawer';
import Timer from './Timer';
import useSessionData, { SessionData } from './useSessionData';
import useSessionToken from './useSessionToken';
import useSignals from './useSignals';
import WaitingScreen from './WaitingScreen';

import cameraBlocked from 'assets/images/call/camera-blocked.png';
import firefoxCameraBlocked from 'assets/images/call/firefox-camera-blocked.png';
import pinIcon from 'assets/images/call/pin.svg';
import safariCameraPermissions from 'assets/images/call/safari-camera-permissions.png';
import safariPreferences from 'assets/images/call/safari-preferences.png';
import { useModal } from 'mui-modal-provider';
import { User } from 'types/user';
import AntSwitch from './AntSwitch';
import BackgroundConfigCard from './BackgroundConfigCard';
import MicrophoneBlockedDialog from './dialogs/MicrophoneBlockedDialog';
import SessionNotes from './SessionNotes';
import WhiteBoard from './Whiteboard';

const apiKey = process?.env?.REACT_APP_TOKBOX_API_KEY ?? '';
let sessionInitialized = false;

const isMicAvailable = async () => {
  try {
    await navigator.mediaDevices.getUserMedia({ audio: true });
    console.log('MicAvailable');
    return true;
  } catch (error: any) {
    console.error('Mic Unavailable', error?.name, ':', error?.message);
    return false;
  }
};

export default function Session() {
  const navigate = useNavigate();
  const sessionId = useParams()?.sessionId?.split(' ')[0];

  const challenge = useSearchParam('challenge');
  const { token } = useSessionToken(sessionId, challenge);
  const { sessionData, reportError, sessionError, loadingSessionData } = useSessionData(sessionId, challenge);
  const { userId } = useAuth();
  const { user: loggedUser, isClient, isProvider, isLoading: isUserLoading } = useUser(userId);

  const { showModal } = useModal();

  const [isMicrophoneAvailable, setIsMicrophoneAvailable] = useState<boolean | undefined>(undefined);

  const timezone = useMemo(() => {
    if (!sessionData || !loggedUser) return 'America/Toronto';

    const owner = sessionData?.owner;
    let ownerObj;
    if (loggedUser?.settings?.timezone) return loggedUser?.settings?.timezone;

    if (sessionData?.participants?.length) {
      ownerObj = sessionData?.participants?.find((p) => p?.user?._id === owner);
    }

    return ownerObj?.user?.settings?.timezone ?? 'America/Toronto';
  }, [loggedUser, sessionData]);

  useEffect(() => {
    if (sessionInitialized) {
      window.location.reload();
    }

    (async () => {
      const isMicrophoneAvailable = await isMicAvailable();
      setIsMicrophoneAvailable(isMicrophoneAvailable);

      if (!isMicrophoneAvailable) {
        showModal(MicrophoneBlockedDialog);
      }
    })();
  }, [setIsMicrophoneAvailable, showModal]);

  const [hasSessionPassed, isEarly] = useMemo(() => {
    if (!sessionData) return [false, false];

    const endTime = moment(sessionData.date).add(sessionData.duration, 'minutes');
    const today = moment();

    return [endTime.diff(today, 'minutes') < -120, moment(sessionData.date).diff(today, 'minutes') > 30];
  }, [sessionData]);

  if (sessionError && !sessionData && !loadingSessionData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
        <MainCard>
          <Stack sx={{ minWidth: '50vw', justifyContent: 'center', alignItems: 'center' }}>
            <Typography sx={(theme) => ({ marginBottom: 2, fontSize: 18, fontWeight: '500', color: theme.palette.error.main })}>
              {(sessionError as any)?.response?.data?.message}
            </Typography>
          </Stack>
        </MainCard>
      </Box>
    );
  }

  if (isUserLoading || !sessionId || !token || !sessionData) return <CenteredLoading />;

  if (hasSessionPassed) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
        <MainCard>
          <Stack>
            <Typography sx={(theme) => ({ marginBottom: 2, fontSize: 18, fontWeight: '500', color: theme.palette.error.main })}>
              <FormattedMessage
                defaultMessage="This session has expired, has ended or is not
              available at the moment"
              />
            </Typography>

            <Button onClick={() => navigate('/')} variant="contained" sx={{ alignSelf: 'center' }}>
              Continue
            </Button>
          </Stack>
        </MainCard>
      </Box>
    );
  }

  if (isEarly) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
        <MainCard>
          <Stack>
            <Typography
              sx={(theme) => ({
                marginBottom: 2,
                fontSize: 18,
                textAlign: 'center',
                fontWeight: '500',
                color: theme.palette.error.main,
                lineHeight: 1.8
              })}
            >
              <FormattedMessage
                defaultMessage="This session will start at {date}."
                values={{
                  date: moment(sessionData?.date).tz(timezone).format('dddd MMMM Do YYYY h:mm A z')
                }}
              />
              <br />
              <FormattedMessage defaultMessage="Your session will be available 30 minutes prior to the scheduled start time." />
            </Typography>

            <Button onClick={() => window.location.reload()} variant="contained" sx={{ alignSelf: 'center' }}>
              Click to Refresh
            </Button>
          </Stack>
        </MainCard>
      </Box>
    );
  }

  if (sessionData?.deleted) {
    const mdate = moment(sessionData.date).tz(timezone);
    const day = mdate.format('dddd MMMM Do YYYY');
    const time = mdate.format('h:mm A z');

    return (
      <Typography variant="h4" sx={{ p: 3, textAlign: 'center' }}>
        <FormattedMessage defaultMessage={'Your session on'} /> {day} at {time}{' '}
        <FormattedMessage defaultMessage={'has been canceled. Please connect with your provider to reschedule.'} />
      </Typography>
    );
  }

  if (!isMicrophoneAvailable) return <Box />;

  return (
    <Box id="sessionContainer" m={0} p={0} sx={{ width: '100%', height: '100%' }}>
      <SessionView
        sessionId={sessionId}
        token={token}
        challenge={challenge}
        loggedUser={loggedUser}
        userId={userId}
        isClient={!loggedUser ? true : isClient}
        isProvider={isProvider}
        sessionData={sessionData}
        reportError={reportError}
      />
    </Box>
  );
}

function SessionView({
  sessionId,
  token,
  challenge,
  loggedUser,
  userId,
  isClient,
  isProvider,
  sessionData,
  reportError
}: {
  sessionId: string;
  token: string;
  challenge?: string | null;
  loggedUser?: User;
  userId?: string;
  isClient: boolean;
  isProvider: boolean;
  sessionData: SessionData;
  reportError: Function;
}) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const { request } = useAuth();

  const handle = useFullScreenHandle();

  const sessionRef = useRef<any>();
  const publisherRef = useRef<any>();
  const screenPublisherRef = useRef<any>();
  const subscribersRef = useRef<any>([]);

  const [initialized, setInitialized] = useState(false);

  const draggableRef = useRef<Draggable | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const pinImageRef = useRef<HTMLImageElement>(null);

  const [bottomToolbarOpen, setBottomToolbarOpen] = useState(true);

  const [sharingScreen, setSharingScreen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread, unreadRef] = useStateRef({});

  const [screenName, setScreenName, screenNameRef] = useStateRef('');
  const [showPromptSave, setShowPromptSave] = useState(false);

  const [selectedSpeaker, setSelectedSpeaker] = useState('default');
  const [selectedMic, setSelectedMic] = useState(undefined);
  const [selectedCamera, setSelectedCamera] = useState(undefined);
  const [isEndSessionDialogOpen, setEndSessionDialogOpen] = useState(false);
  const [dialogMessage, setDialogMessage] = useState<string | null>(null);
  const [cameraAccessDeniedDialogOpen, setCameraAccessDeniedDialogOpen] = useState(false);

  const [closingAnnotation, setClosingAnnotation] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<Date | undefined>();

  const dispatch = useDispatch();

  const handleError = useCallback(
    (error) => {
      if (error) {
        console.error('...handleError', error);
        try {
          reportError({ error, errorName: String(error), userId, challenge, date: new Date() });
        } catch (error) {
          console.error('...Error handleError', error);
        }
      }
    },
    [reportError, userId, challenge]
  );

  useEffect(() => {
    // Hide menu
    setTimeout(() => {
      dispatch({ type: SET_MENU, opened: false });
    }, 1000);

    // Prevent login session timeout during session
    const intervalId = setInterval(() => {
      localStorage.setItem('lastActivityTimeStamp', String(moment().unix()));
    }, 30000);

    return () => clearInterval(intervalId);
  }, [dispatch]);

  const floatingEmail = useMemo(() => {
    setSessionStartTime(sessionData?.startTime);
    if (!challenge) {
      return null;
    }
    const participant = sessionData?.floatingParticipants.find((participant) => participant.challenge === challenge);

    return participant?.email ?? null;
  }, [sessionData, challenge]);

  const attendMutation = useMutation(() => request.post(`/session/${sessionId}/participant/${userId}/attend`));
  const { mutate } = attendMutation;
  useEffect(() => {
    if (!challenge) {
      mutate();
    }
  }, [mutate, challenge]);

  const endMutation = useMutation(() => request.post(`/session/${sessionId}`, { endTime: new Date() }));

  const setSessionStartTimeMutation = useMutation((startTime: Date) => request.post(`/session/${sessionId}`, { startTime }));

  const startSessionCountdown = useCallback(() => {
    const startTime = new Date();
    setSessionStartTimeMutation.mutate(startTime);
    setSessionStartTime(startTime);
  }, [setSessionStartTimeMutation, setSessionStartTime]);

  const isOwner = userId === sessionData?.owner;
  const isTherapist = loggedUser?.role?.includes('PROVIDER') ?? false;

  const {
    connectedUsers,
    isWaitingScreen = true,
    allMuted,
    setMuteAll = () => {},
    publishVideo = false,
    publishAudio = false,
    setPublishVideo = () => {},
    setPublishAudio = () => {},
    isJoinPrompt,
    setJoinPrompt,
    isParticipantRemoved,
    sessionStarted = false,
    setSessionStarted = () => {},
    arrived,
    emitOpenWhiteboard,
    changeLayout = () => {},
    oneToOneChatEnabled = false,
    setOneToOneChatEnabledAndSignal = () => {},
    enableParticipantsToAnnotate,
    setEnableParticipantsToAnnotateAndSignal,
    cameraGranted,
    setCameraGranted,
    setupSignals,
    handleLayout = () => {},
    activeLayoutRef,
    layoutBeforeRef,
    whiteboardOpen = false,
    setWhiteboardOpen = () => {},
    whiteboardOpenRef,
    annotate,
    annotateRef,
    pinnedStreamIdRef,
    arrivedRef
  } = useSignals({
    sessionRef,
    floatingEmail,
    screenNameRef,
    isOwner,
    publisherRef,
    subscribersRef,
    pinImageRef,
    handleError,
    isClient,
    isProvider
  });

  const turnOffScreenShare = useCallback(() => {
    if (!sessionRef.current) return;

    if (pinnedStreamIdRef) {
      pinnedStreamIdRef.current = null;
    }

    const stream = screenPublisherRef.current;
    if (stream) {
      subscribersRef.current = subscribersRef.current.filter((subscriber) => subscriber.streamId !== stream.streamId);
      screenPublisherRef.current.destroy();
      screenPublisherRef.current = null;
    }

    sessionRef.current.signal({ type: 'stoppedSharing' }, (error) => {
      if (!error) {
        setSharingScreen(false);
      }
    });
    setWhiteboardOpen(false);
    changeLayout(layoutBeforeRef?.current || 'grid');
  }, [sessionRef, changeLayout, setWhiteboardOpen, layoutBeforeRef, pinnedStreamIdRef]);

  const toggleScreenShare = useCallback(() => {
    if (sharingScreen) {
      turnOffScreenShare();
    } else {
      OT.checkScreenSharingCapability(async (r) => {
        if (!r.supported || r.extensionRegistered === false) {
          enqueueSnackbar(
            intl.formatMessage({
              defaultMessage: 'Your browser does not support screen sharing.'
            })
          );
        } else {
          try {
            let media;
            if ((window as any).safari) {
              media = await (navigator.mediaDevices as any).getDisplayMedia({
                video: {
                  cursor: 'always'
                },
                audio: false
              });
              media.getVideoTracks()[0].addEventListener('ended', turnOffScreenShare);
            }

            sessionRef.current.signal({ type: 'startedSharing' }, (error) => {
              if (!error) {
                const publisherDiv = document.getElementById('subscribersGrid')!;

                screenPublisherRef.current = OT.initPublisher(
                  publisherDiv,
                  {
                    insertMode: 'append',
                    width: '100%',
                    height: '100%',
                    publishAudio: true,
                    publishVideo: true,
                    videoSource: media ? media.getVideoTracks()[0] : 'screen',
                    name: 'screen',
                    fitMode: 'contain',
                    showControls: false
                  },
                  handleError
                );

                screenPublisherRef.current.on({
                  accessAllowed: () => {
                    sessionRef.current?.publish(screenPublisherRef.current, () => {
                      if (error) {
                        console.error('Error in screen share:', error);
                      }
                      subscribersRef.current.push(screenPublisherRef.current);
                      setSharingScreen(true);
                      if (layoutBeforeRef?.current) {
                        layoutBeforeRef.current = activeLayoutRef.current || 'grid';
                      }
                      if (pinnedStreamIdRef) {
                        pinnedStreamIdRef.current = null;
                      }
                      changeLayout('left-sidebar');
                    });
                  },
                  mediaStopped: () => {
                    turnOffScreenShare();
                  },
                  accessDenied: (e) => {
                    console.log('Screen sharing permissions denied:', screenPublisherRef.current);
                    turnOffScreenShare();
                  }
                });
              }
            });
          } catch (error) {
            console.error(error);
            handleError(error);
          }
        }
      });
    }
  }, [
    sharingScreen,
    enqueueSnackbar,
    intl,
    turnOffScreenShare,
    handleError,
    changeLayout,
    activeLayoutRef,
    layoutBeforeRef,
    pinnedStreamIdRef
  ]);

  const hasMediaProcessorSupport = useMemo(() => {
    return (OT as any).hasMediaProcessorSupport();
  }, []);

  useEffect(() => {
    if (sessionStarted) {
      setTimeout(() => {
        setBottomToolbarOpen(false);
      }, 7000);
    }
  }, [sessionStarted, loggedUser?.imageSmall]);

  useEffect(() => {
    const initializeSession = () => {
      const publisherDiv = document.getElementById('subscribersGrid')!;
      publisherDiv.innerText = '';

      sessionRef.current = OT.initSession(apiKey, sessionId);
      sessionInitialized = true;

      publisherRef.current = OT.initPublisher(
        publisherDiv,
        {
          insertMode: 'append',
          width: '100%',
          height: '100%',
          publishVideo: false,
          publishAudio: false,
          showControls: false,
          style: {
            backgroundImageURI: loggedUser?.imageSmall,
            archiveStatusDisplayMode: 'auto'
          }
        },
        handleError
      );

      // Connect to the session
      sessionRef.current?.connect(token, (error) => {
        if (error) {
          handleError(error);
        } else {
          if (setupSignals) {
            localStorage.setItem('token', token);
            setInitialized(true);
            setupSignals(token);
          }
        }
      });
    };

    initializeSession();

    return () => {
      sessionRef.current?.disconnect();
      sessionRef.current = null;
    };
  }, [sessionData, token, sessionId, setupSignals, handleError, hasMediaProcessorSupport, loggedUser]);

  const isCameraAvailable = useCallback(async () => {
    if (!setCameraGranted) return;

    try {
      await navigator.mediaDevices.getUserMedia({ video: { deviceId: selectedCamera } });
      setCameraGranted(true);
      return true;
    } catch (error: any) {
      setCameraAccessDeniedDialogOpen(true);
      console.error(error?.name, ':', error?.message);
      handleError(error);
      return false;
    }
  }, [setCameraGranted, selectedCamera, handleError]);

  return (
    <FullScreen handle={handle} className={classes.root}>
      {sessionData && sessionStartTime && isOwner && (
        <Timer startTime={sessionStartTime} duration={sessionData.duration} sessionStarted sessionEnded={!!sessionData?.endTime} />
      )}

      {sessionData && notesOpen && <SessionNotes sessionData={sessionData} onClose={() => setNotesOpen(false)} />}

      {setCameraGranted && (
        <Draggable ref={draggableRef} bounds="#sessionContainer">
          <div
            hidden={(sessionStarted && arrived) || !publishVideo}
            style={{
              zIndex: 1200,
              width: 300,
              height: 200,
              position: 'absolute',
              top: window.innerHeight * 0.1,
              right: 20
            }}
          >
            <CameraPreview selectedCamera={selectedCamera} setCameraGranted={setCameraGranted} />
          </div>
        </Draggable>
      )}

      <>
        <BasicDialog
          container={containerRef.current}
          open={!!dialogMessage}
          onClose={() => setDialogMessage(null)}
          maxWidth="sm"
          actions={
            <>
              <Button variant="contained" onClick={() => setDialogMessage(null)}>
                <FormattedMessage defaultMessage="OK" />
              </Button>
            </>
          }
        >
          {dialogMessage}
        </BasicDialog>

        {setJoinPrompt && (
          <BasicDialog
            container={containerRef.current}
            open={isJoinPrompt ?? false}
            onClose={() => setJoinPrompt(false)}
            maxWidth="sm"
            actions={
              <>
                <Button variant="contained" onClick={() => setJoinPrompt(false)}>
                  <FormattedMessage defaultMessage="OK" />
                </Button>
              </>
            }
          >
            <Box sx={{ py: 1 }}>
              {!sessionStarted
                ? isProvider
                  ? intl.formatMessage({ defaultMessage: 'Your patient has arrived. You can start the session.' })
                  : intl.formatMessage({ defaultMessage: 'Your provider has arrived. You can start the session.' })
                : intl.formatMessage({ defaultMessage: 'Your camera/microphone is being used for the session' })}
            </Box>
          </BasicDialog>
        )}

        <BasicDialog container={containerRef.current} open={isParticipantRemoved ?? false} maxWidth="sm">
          <Box sx={{ py: 1 }}>
            <FormattedMessage defaultMessage={'You have been removed from session by the Provider'} />
          </Box>
        </BasicDialog>

        <BasicDialog
          container={containerRef.current}
          open={isEndSessionDialogOpen}
          onClose={() => setEndSessionDialogOpen(false)}
          maxWidth="sm"
          actions={
            <>
              <Button variant="contained" onClick={() => setEndSessionDialogOpen(false)}>
                <FormattedMessage defaultMessage="No" />
              </Button>
              <Button
                variant="contained"
                onClick={() => {
                  if (isOwner) {
                    endMutation.mutate();
                  }
                  sessionRef.current?.disconnect();
                  window.location.replace('/appointments');
                }}
              >
                <FormattedMessage defaultMessage="Yes" />
              </Button>
            </>
          }
        >
          <Box sx={{ py: 1 }}>
            <FormattedMessage defaultMessage={'Are you sure you want to leave the session?'} />
          </Box>
        </BasicDialog>

        {sessionData && setPublishVideo && (
          <ScreenName
            initialized={initialized}
            containerRef={containerRef}
            publisherRef={publisherRef}
            sessionRef={sessionRef}
            sessionStarted={sessionStarted}
            setSessionStarted={setSessionStarted}
            screenName={screenName}
            setScreenName={setScreenName}
            publishVideo={publishVideo}
            publishAudio={publishAudio}
            setPublishVideo={setPublishVideo}
            setPublishAudio={setPublishAudio}
            floatingEmail={floatingEmail}
            isCameraAvailable={isCameraAvailable}
            arrivedRef={arrivedRef}
          />
        )}

        <BasicDialog
          open={cameraAccessDeniedDialogOpen}
          onClose={() => {
            setCameraAccessDeniedDialogOpen(false);
          }}
          container={containerRef.current}
          maxWidth="xs"
          title={intl.formatMessage({ defaultMessage: 'Camera is blocked' })}
          actions={
            <>
              <Button
                variant="contained"
                onClick={() => {
                  setCameraAccessDeniedDialogOpen(false);
                }}
              >
                <FormattedMessage defaultMessage="OK" />
              </Button>
            </>
          }
        >
          {browserName === 'Safari' ? (
            <>
              <FormattedMessage defaultMessage={' Please enable access to your Camera.'} />
              <br />
              <FormattedMessage defaultMessage={'Go to'} />{' '}
              <b>
                <FormattedMessage defaultMessage="{Safari}" />
              </b>{' '}
              &gt;{' '}
              <b>
                {' '}
                <FormattedMessage defaultMessage={'Preferences'} />
              </b>{' '}
              <FormattedMessage defaultMessage={'and then allow access to Camera.'} />
              <br />
              <img alt="preferences" src={safariPreferences} style={{ width: 160, marginTop: 20 }} />
              <img alt="cam" src={safariCameraPermissions} style={{ width: 180, marginLeft: 20 }} />
            </>
          ) : (
            <>
              <FormattedMessage defaultMessage={'Please enable access to your Camera. Click the camera blocked icon'} />
              <img
                alt="cam"
                src={browserName === 'Chrome' ? cameraBlocked : firefoxCameraBlocked}
                style={{ width: 25, position: 'relative', left: 4, top: 8, marginRight: 5 }}
              />{' '}
              <FormattedMessage defaultMessage={'in your browser'} />
              &apos; <FormattedMessage defaultMessage={'s address bar.'} />
            </>
          )}
        </BasicDialog>
      </>

      <Box ref={containerRef} style={{ flex: 1 }} position="relative" height={challenge ? '98vh' : '100%'}>
        {isWaitingScreen && <WaitingScreen isProvider={isProvider} />}

        <Box
          m={0}
          p={0}
          sx={(theme) => ({ position: 'absolute', width: '100%', height: '100%', backgroundColor: theme.palette.primary.light })}
          hidden={isWaitingScreen}
        >
          <div id="subscribersGrid" />

          <div id="leftView">
            <div
              className="leftPanel"
              style={{
                height: challenge ? '90vh' : '78vh'
              }}
            />
            <div className="rightPanel" />
          </div>

          <div id="justMe" />
          <div id="pinned" />

          <Draggable ref={draggableRef} bounds="#sessionContainer">
            <div id="pipView" />
          </Draggable>

          <div id="hiddenGrid" hidden />

          {initialized && connectedUsers && Object.keys(connectedUsers)?.length ? (
            <WhiteBoard
              containerRef={containerRef}
              subscribersRef={subscribersRef}
              whiteboardOpen={whiteboardOpen}
              whiteboardOpenRef={whiteboardOpenRef}
              setWhiteBoardOpen={setWhiteboardOpen}
              sessionRef={sessionRef}
              annotate={annotate}
              isProvider={isProvider}
              isOwner={isOwner}
              appointmentId={sessionData.appointmentId}
              setDialogMessage={setDialogMessage}
              showPromptSave={showPromptSave}
              setShowPromptSave={setShowPromptSave}
              closingAnnotation={closingAnnotation}
              setClosingAnnotation={setClosingAnnotation}
              chatOpen={chatOpen}
              changeLayout={changeLayout}
              floatingEmail={floatingEmail}
              sessionData={sessionData}
            />
          ) : null}
          <img
            ref={pinImageRef}
            id="pinIcon"
            className="hide"
            src={pinIcon}
            style={{ zIndex: 3, width: 40, height: 40, backgroundColor: '#337ab7', borderRadius: '10px', padding: '5px' }}
            draggable={false}
            alt="pin"
          />
        </Box>

        <Stack alignItems="center" position="absolute" bottom={0} sx={{ zIndex: 3, left: '50%', transform: 'translate(-50%)' }}>
          <IconButton
            onClick={() => {
              setBottomToolbarOpen(!bottomToolbarOpen);
            }}
            style={{
              backgroundColor: 'rgba(51,51,51,.7)',
              borderRadius: 0,
              height: 28
            }}
          >
            {bottomToolbarOpen ? <IconChevronDown style={{ color: '#fff' }} /> : <IconChevronUp style={{ color: '#fff' }} />}
          </IconButton>

          <Stack
            alignItems="center"
            style={{
              height: bottomToolbarOpen ? 'auto' : 0,
              transition: 'height 240ms ease-in-out, padding 240ms ease-in-out',
              overflow: 'hidden'
            }}
          >
            <ButtonGroup color="primary" variant="contained">
              {initialized && hasMediaProcessorSupport && sessionStarted && arrived && (
                <BackgroundConfigCard
                  containerRef={containerRef}
                  publisherRef={publisherRef}
                  publishVideo={publishVideo}
                  sessionStarted={sessionStarted}
                />
              )}

              <ControlButton
                containerRef={containerRef}
                startIcon={publishAudio ? <IconMicrophone size={20} /> : <IconMicrophoneOff size={20} />}
                onClick={() => setPublishAudio(!publishAudio)}
              >
                {publishAudio ? <FormattedMessage defaultMessage="Mute" /> : <FormattedMessage defaultMessage="Unmute" />}
              </ControlButton>
              <ControlButton
                containerRef={containerRef}
                startIcon={publishVideo ? <IconCamera size={20} /> : <IconCameraOff size={20} />}
                onClick={async () => {
                  // If camera permissions not granted
                  if (!(await isCameraAvailable())) {
                    setPublishVideo(false);
                    return;
                  }
                  setPublishVideo(!publishVideo);
                }}
              >
                {publishVideo ? <FormattedMessage defaultMessage="Camera Off" /> : <FormattedMessage defaultMessage="Camera On" />}
              </ControlButton>
              {isProvider && (
                <ControlButton
                  containerRef={containerRef}
                  startIcon={allMuted ? <IconVolume3 size={20} /> : <IconVolume size={20} />}
                  onClick={() => setMuteAll(!allMuted)}
                >
                  {allMuted ? <FormattedMessage defaultMessage="Unmute All" /> : <FormattedMessage defaultMessage="Mute All" />}
                </ControlButton>
              )}

              {sessionStarted && arrived && (
                <LayoutButton
                  containerRef={containerRef}
                  onLayoutChange={(layout) => {
                    if (layoutBeforeRef?.current) {
                      layoutBeforeRef.current = layout;
                    }
                    changeLayout(layout);
                  }}
                  draggableRef={draggableRef}
                  whiteboardOpen={whiteboardOpen}
                />
              )}
            </ButtonGroup>
            <ButtonGroup color="primary" variant="contained">
              {!!loggedUser && ((isOwner && !sessionStartTime) || !isOwner) ? (
                <ControlButton
                  containerRef={containerRef}
                  startIcon={<IconPlayerStop size={20} color="#e81717" />}
                  onClick={() => (!isOwner || !sessionStartTime ? setEndSessionDialogOpen(true) : null)}
                >
                  <Box component="span" style={{ color: '#e81717', margin: 0 }}>
                    <FormattedMessage defaultMessage="End Session" />
                  </Box>
                </ControlButton>
              ) : null}

              {isOwner && (
                <ControlButton
                  containerRef={containerRef}
                  startIcon={
                    sessionStartTime ? (
                      <IconPlayerStop size={20} color="#e81717" />
                    ) : (
                      <IconPlayerPlay size={20} style={{ position: 'relative', top: 2 }} />
                    )
                  }
                  onClick={() => (!sessionStartTime ? startSessionCountdown() : setEndSessionDialogOpen(true))}
                >
                  {!sessionStartTime ? (
                    <Box component="span" style={{ margin: 0 }}>
                      <FormattedMessage defaultMessage="Session Clock" />
                    </Box>
                  ) : (
                    <Box component="span" style={{ color: '#e81717', margin: 0 }}>
                      <FormattedMessage defaultMessage="End Session" />
                    </Box>
                  )}
                </ControlButton>
              )}

              {sessionStarted && arrived && !sessionData?.disableShareScreen && (
                <ControlButton
                  containerRef={containerRef}
                  startIcon={sharingScreen ? <IconScreenShareOff size={20} /> : <IconScreenShare size={20} />}
                  onClick={toggleScreenShare}
                  isSelected={sharingScreen}
                >
                  {sharingScreen ? <FormattedMessage defaultMessage="Stop Sharing" /> : <FormattedMessage defaultMessage="Screen Share" />}
                </ControlButton>
              )}

              <ControlButton
                containerRef={containerRef}
                startIcon={handle.active ? <IconArrowsMinimize size={20} /> : <IconArrowsMaximize size={20} />}
                onClick={() => {
                  if (handle.active) {
                    handle.exit();
                  } else {
                    handle.enter();
                  }
                }}
              >
                <FormattedMessage defaultMessage="Full Screen" />
              </ControlButton>

              {!challenge && !sessionData?.disableNotes && (
                <ControlButton
                  containerRef={containerRef}
                  startIcon={<IconNote size={20} />}
                  onClick={() => setNotesOpen(!notesOpen)}
                  isSelected={notesOpen}
                >
                  {!notesOpen ? <FormattedMessage defaultMessage="Open Notes" /> : <FormattedMessage defaultMessage="Close Notes" />}
                </ControlButton>
              )}

              {sessionStarted && arrived && connectedUsers && !sessionData?.disableAnnotation && !!Object.keys(connectedUsers)?.length && (
                <ControlButton
                  containerRef={containerRef}
                  startIcon={<IconWriting size={20} />}
                  onClick={() => {
                    // if (!whiteboardOpen && emitOpenWhiteboard) {
                    if (!whiteboardOpenRef.current) {
                      if (isOwner) {
                        setDialogMessage('Annotation will be auto saved every 30 seconds in Client Records');
                      }
                      emitOpenWhiteboard(sharingScreen);
                      setClosingAnnotation(false);
                    }

                    let changeLayoutToBefore = false;
                    if (whiteboardOpenRef.current) {
                      changeLayoutToBefore = true;
                    }
                    if (whiteboardOpenRef.current && isOwner) {
                      setClosingAnnotation(true);
                    } else {
                      setWhiteboardOpen((pre) => !pre);

                      if (changeLayoutToBefore) {
                        changeLayout(layoutBeforeRef.current);
                      }
                    }
                  }}
                  isSelected={whiteboardOpen}
                  subMenu={
                    isProvider ? (
                      <MenuItem onClick={(e) => e.stopPropagation()}>
                        <FormControlLabel
                          sx={{
                            color: '#fff',
                            fontSize: 13,
                            padding: '3px 10px 3px 2px',
                            marginLeft: 0,
                            borderRadius: (theme) => theme.shape.borderRadius
                          }}
                          control={
                            <AntSwitch
                              checked={enableParticipantsToAnnotate}
                              size="small"
                              onClick={(event) => {
                                event.stopPropagation();
                                setEnableParticipantsToAnnotateAndSignal(!enableParticipantsToAnnotate);
                              }}
                            />
                          }
                          label={intl.formatMessage({ defaultMessage: 'Enable participants to Annotate' })}
                          labelPlacement="start"
                        />
                      </MenuItem>
                    ) : null
                  }
                  disabled={!isOwner && !enableParticipantsToAnnotate && !whiteboardOpen}
                >
                  {!whiteboardOpen ? (
                    <FormattedMessage defaultMessage="Annotate" />
                  ) : (
                    <FormattedMessage defaultMessage="Close Annotation" />
                  )}
                </ControlButton>
              )}

              <ControlButton
                containerRef={containerRef}
                startIcon={
                  <Badge color="error" badgeContent={Object.keys(unread).length}>
                    <IconUsers size={20} />
                  </Badge>
                }
                onClick={() => setChatOpen((pre) => !pre)}
                isSelected={chatOpen}
              >
                <FormattedMessage defaultMessage="Participants" />
              </ControlButton>
            </ButtonGroup>
          </Stack>
        </Stack>
      </Box>

      {initialized && (
        <SessionSideDrawer
          containerRef={containerRef}
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          floatingEmail={floatingEmail}
          appointmentId={sessionData?.appointmentId}
          sessionId={sessionId}
          unread={unread}
          unreadRef={unreadRef}
          setUnread={setUnread}
          sessionRef={sessionRef}
          sessionData={sessionData}
          connectedUsers={connectedUsers}
          selectedSpeaker={selectedSpeaker}
          setSelectedSpeaker={setSelectedSpeaker}
          selectedMic={selectedMic}
          setSelectedMic={setSelectedMic}
          selectedCamera={selectedCamera}
          setSelectedCamera={setSelectedCamera}
          isWaitingScreen={isWaitingScreen}
          role={loggedUser?.role ?? 'CLIENT'}
          screenName={screenName}
          arrived={arrived}
          oneToOneChatEnabled={oneToOneChatEnabled}
          setOneToOneChatEnabled={setOneToOneChatEnabledAndSignal}
          isOwner={isOwner}
          isProvider={isProvider}
          ownerId={sessionData.owner}
        />
      )}
    </FullScreen>
  );
}

const useStyles = makeStyles({
  root: {
    display: 'flex',

    '& .OT_root': {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    },

    '& .OT_video-poster': {
      backgroundPosition: 'center'
    },

    '& #subscribersGrid': {
      padding: 5,
      width: '100%',
      height: '100%',
      display: 'grid',
      gap: 2
    },

    '& #leftView': {
      display: 'none',
      height: '100%',
      justifyContent: 'space-between',
      gridTemplateColumns: '1fr 3fr',

      '& .leftPanel': {
        margin: 3,
        overflowY: 'scroll',
        overflowX: 'hidden',
        '& > div': {
          height: '320px !important',
          marginBottom: 2
        },
        '&::-webkit-scrollbar': {
          display: 'none'
        }
      },

      '& .rightPanel': {
        margin: 3,
        display: 'grid',
        columnGap: 2,
        rowGap: 2
      }
    },

    '& #justMe': { display: 'none', width: '100%', height: '100%' },
    '& #pinned': { display: 'none', width: '100%', height: '100%' },

    '& #pipView': {
      zIndex: 1300,
      width: 300,
      height: 200,
      display: 'none',
      position: 'absolute',
      top: window.innerHeight * 0.1,
      right: 10,
      '@media (max-width:600px)': {
        width: 150,
        height: 150
      }
    },

    '& #pinIcon': {
      cursor: 'pointer',

      '&.show': {
        display: 'block'
      },
      '&.hide': {
        display: 'none'
      }
    }
  }
});
